// Cvanto Search Phase 4B — requester's own Buyer Request history.
// Mirrors src/components/Settings/Balance/Balance.js's exact
// loading/error/empty/list/"Показати ще" pattern (closest existing
// precedent for a Settings sub-page backed by a cursor-paginated list).
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getAccessToken } from "../../../awsAuth";
import "./MyRequests.scss";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const STATUS_LABELS = {
  active: "Активний",
  fulfilled: "Виконано",
  cancelled: "Скасовано",
  expired: "Завершився",
};

function statusLabel(effectiveStatus) {
  return STATUS_LABELS[effectiveStatus] || effectiveStatus || "";
}

function statusTone(effectiveStatus) {
  if (effectiveStatus === "active") return "active";
  if (effectiveStatus === "fulfilled") return "success";
  return "muted"; // cancelled / expired
}

function formatBudget(budget) {
  if (!budget) return "";
  const currency = budget.currency === "UAH" ? "грн" : budget.currency || "";
  if (budget.min != null && budget.max != null) return `${budget.min}–${budget.max} ${currency}`.trim();
  if (budget.max != null) return `до ${budget.max} ${currency}`.trim();
  if (budget.min != null) return `від ${budget.min} ${currency}`.trim();
  return "";
}

function formatDate(ms) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString("uk-UA", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function MyRequests() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (nextCursor) => {
    try {
      if (nextCursor) setLoadingMore(true);
      else setLoading(true);
      setError("");

      const token = await getAccessToken().catch(() => null);
      const qs = nextCursor ? `?cursor=${encodeURIComponent(nextCursor)}` : "";

      const res = await fetch(`${API_BASE}/api/buyer-requests/mine${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      setItems((prev) => (nextCursor ? [...prev, ...(data.items || [])] : data.items || []));
      setCursor(data.cursor || null);
    } catch (e) {
      setError("Не вдалося завантажити ваші запити");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    load(null);
  }, [load]);

  return (
    <main className="myRequestsPage">
      <header className="myRequestsPage__hero">
        <button
          type="button"
          className="myRequestsPage__back"
          onClick={() => navigate(-1)}
          aria-label="Назад"
        >
          <ArrowLeft size={22} strokeWidth={2.5} />
        </button>
        <h1>Мої запити</h1>
      </header>

      <section className="myRequestsList">
        {loading ? (
          <div className="myRequestsList__state">Завантаження...</div>
        ) : error ? (
          <div className="myRequestsList__state myRequestsList__state--error">{error}</div>
        ) : items.length === 0 ? (
          <div className="myRequestsList__state">У вас ще немає запитів</div>
        ) : (
          <ul className="myRequestsList__items">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="myRequestsList__card"
                  onClick={() => navigate(`/request/${item.id}`)}
                >
                  <div className="myRequestsList__card-main">
                    <span className="myRequestsList__card-query">{item.query}</span>
                    <span className="myRequestsList__card-meta">
                      {[item.city, formatBudget(item.budget)].filter(Boolean).join(" · ")}
                    </span>
                    <span className="myRequestsList__card-date">{formatDate(item.createdAt)}</span>
                  </div>
                  <span
                    className={`myRequestsList__badge is-${statusTone(item.effectiveStatus)}`}
                  >
                    {statusLabel(item.effectiveStatus)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {cursor && !loading && (
          <button
            type="button"
            className="myRequestsList__more"
            onClick={() => load(cursor)}
            disabled={loadingMore}
          >
            {loadingMore ? "Завантаження..." : "Показати ще"}
          </button>
        )}
      </section>
    </main>
  );
}
